import React, { useEffect, useState } from "react";
import { Form, Field } from "react-final-form";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, Col, Row } from "react-bootstrap";
import {
  getBusinessProfile,
  postBusinessProfile,
} from "../../../services/BusinessService";
import {
  getServicesType,
  getBusinessType,
} from "../../../services/GeneralService";
import {
  getBusinesProfileHandler,
  postBusinessProfileHandler,
} from "../../../services/Handlers/BusinessProfileService";

import { useLocalization } from "../../../context/LocalizeContextProvider";
import { useNotification } from "../../../context/NotificationProvider";
import TitlePage from "../../components/TitlePage";
import InputField from "../../components/InputField";
import CheckboxField from "../../components/CheckboxField";
import AsyncSelect from "../../components/AsyncSelect";
import Button from "../../components/Button";
import UploadImageAvatar from "../../components/UploadImageAvatar";
import LoadingSection from "../../components/LoadingSection";
import { retrieveUrlFile } from "../../../utils/utils";
import PrimaryColorField from "../../components/PrimaryColorField";
import { useAuth } from "../../../context/AuthProvider";

export default function BusinessProfile() {
  const localize = useLocalization();
  const notification = useNotification();
  const { userDetail } = useAuth();

  const [dataServices, setDataServices] = useState([]);
  const [dataBusiness, setDataBusiness] = useState([]);
  const [dataProfile, setDataProfile] = useState({
    businessName: "",
    businessType: null,
    businessService: [],
    businessAddress: "",
    businessLogo: null,
    businessImage: null,
    published: false,
    primaryColor: "",
    successReservationUrl: "",
    about: "",
    yearEstablished: "",
    phoneNumber: "",
    gtmId: "",
  });

  const { data: dataServicesType } = useQuery(
    ["ServicesType"],
    getServicesType,
    {
      refetchOnWindowFocus: false,
    }
  );
  const { data: dataBusinessType } = useQuery(
    ["BusinessType"],
    getBusinessType,
    {
      refetchOnWindowFocus: false,
    }
  );

  const {
    isLoading,
    refetch,
    data: dataProfileFetch,
  } = useQuery(["BusinessProfile"], getBusinessProfile, {
    onSuccess: (res) => {
      if (res.data && res.status === 200) {
        const response = res.data;
        setDataProfile((prevState) => ({
          ...prevState,
          businessName: response?.name,
          businessType: {
            label: response?.businessType.name,
            value: response?.businessType.id,
          },
          businessService: [],
          businessAddress: response?.address,
          businessLogo: response?.logo,
          businessImage: response?.mainPhoto,
          published: response?.published,
          primaryColor: response?.primaryColor,
          successReservationUrl: response?.successReservationUrl,
        }));
        const body = document.querySelector("body");
        body.setAttribute("data-primary", response?.primaryColor);
      }
    },
    refetchOnWindowFocus: false,
  });

  const {
    isLoading: isLoadingBusinessProfile,
    refetch: refetchBusinessProfile,
  } = useQuery(
    ["BusinessProfileHandler", userDetail?.businessId],
    () => getBusinesProfileHandler(userDetail?.businessId),
    {
      onSuccess: (res) => {
        if (res.data && res.status === 200) {
          const response = res?.data?.data;
          if (response) {
            setDataProfile((prevState) => ({
              ...prevState,
              about: response.about,
              yearEstablished: response.year_founded,
              gtmId: response.gtm_id,
              businessName: response.business_name || prevState.businessName,
              phoneNumber: response.phone_number,
              businessLogo: response.image_url || prevState.businessLogo,
              published: response.published || prevState.published,
            }));
          }
        }
      },
      enabled: !!userDetail?.businessId,
      refetchOnWindowFocus: false,
    }
  );

  const { isLoading: isLoadingSubmit, mutate: mutateSubmit } = useMutation(
    postBusinessProfile,
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          notification.success(localize.getText("successAddData"));
          refetch();
        } else {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError")
          );
        }
      },
      onError: (res) => {
        notification.error(localize.getText("unknownMessageError"));
      },
    }
  );

  const {
    isLoading: isLoadingSubmitBusinessProfile,
    mutate: mutateSubmitBusinessProfile,
  } = useMutation(postBusinessProfileHandler, {
    onSuccess: (res) => {
      if (res.status === 200 || res.status === 201) {
        refetchBusinessProfile();
      } else {
        notification.error(
          res?.data?.detail || localize.getText("unknownMessageError")
        );
      }
    },
    onError: (res) => {
      notification.error(localize.getText("unknownMessageError"));
    },
  });

  useEffect(() => {
    if (dataServicesType && dataServicesType.data.length > 0) {
      const data = [];
      dataServicesType.data.forEach((item) => {
        data.push({
          value: item.id,
          label: item.name,
        });
      });
      setDataServices(data);
    }
  }, [dataServicesType]);

  useEffect(() => {
    if (dataBusinessType && dataBusinessType.data.length > 0) {
      const data = [];
      dataBusinessType.data.forEach((item) => {
        data.push({
          value: item.id,
          label: item.name,
        });
      });
      setDataBusiness(data);
    }
  }, [dataBusinessType]);

  useEffect(() => {
    if (dataProfileFetch && dataServices.length > 0) {
      const data = dataProfileFetch.data;
      handleChangeField(
        "businessService",
        dataServices.filter((item) => data.serviceIds.includes(item.value))
      );
    }
  }, [dataProfileFetch, dataServices]);

  const handleChangeField = (name, value) => {
    setDataProfile((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmitForm = async (values) => {
    let logoUrl = values.businessLogo;
    let imageUrl = values.businessImage;
    const serviceIds = [];

    if (values.businessLogo && values.businessLogo.constructor === File) {
      logoUrl = await retrieveUrlFile(values.businessLogo);
    }
    if (values.businessImage && values.businessImage.constructor === File) {
      imageUrl = await retrieveUrlFile(values.businessImage);
    }
    values?.businessService?.forEach((item) => {
      serviceIds.push(item.value);
    });

    const data = {
      name: values.businessName,
      address: values.businessAddress,
      logo: logoUrl,
      mainPhoto: imageUrl,
      businessTypeId: values.businessType.value,
      serviceIds,
      published: values.published,
      primaryColor: values.primaryColor,
      successReservationUrl: values.successReservationUrl,
    };

    const dataProfile = {
      business_id: userDetail?.businessId,
      about: values.about,
      year_founded: values.yearEstablished,
      business_name: values.businessName,
      phone_number: values.phoneNumber,
      image_url: logoUrl,
      gtm_id: values.gtmId,
      published: values.published,
    };

    mutateSubmitBusinessProfile(dataProfile);
    mutateSubmit(data);
  };

  if (isLoading || isLoadingBusinessProfile) {
    return <LoadingSection />;
  }

  return (
    <Form
      initialValues={{
        businessName: dataProfile?.businessName,
        businessType: dataProfile?.businessType,
        businessService: dataProfile?.businessService,
        businessAddress: dataProfile?.businessAddress,
        businessLogo: dataProfile?.businessLogo,
        businessImage: dataProfile?.businessImage,
        published: dataProfile?.published,
        primaryColor: dataProfile?.primaryColor,
        successReservationUrl: dataProfile?.successReservationUrl,
        about: dataProfile?.about || "",
        yearEstablished: dataProfile?.yearEstablished || "",
        phoneNumber: dataProfile?.phoneNumber || "",
        gtmId: dataProfile?.gtmId || "",
      }}
      validate={(values) => {
        const errors = {};

        if (!values.businessName) {
          errors.businessName = localize.getText(
            "generalRequiredFieldValidation",
            { fieldName: localize.getText("businessName") }
          );
        }
        if (!values.businessType) {
          errors.businessType = localize.getText(
            "generalRequiredFieldValidation",
            { fieldName: localize.getText("businessType") }
          );
        }
        if (values.businessService.length === 0) {
          errors.businessService = localize.getText(
            "generalRequiredFieldValidation",
            { fieldName: localize.getText("businessService") }
          );
        }
        if (!values.businessAddress) {
          errors.businessAddress = localize.getText(
            "generalRequiredFieldValidation",
            { fieldName: localize.getText("businessAddress") }
          );
        }
        if (!values.about) {
          errors.about = localize.getText("generalRequiredFieldValidation", {
            fieldName: localize.getText("aboutClinic"),
          });
        }
        if (!values.yearEstablished) {
          errors.yearEstablished = localize.getText(
            "generalRequiredFieldValidation",
            {
              fieldName: localize.getText("yearEstablished"),
            }
          );
        }
        if (values.yearEstablished && values.yearEstablished.length < 4) {
          errors.yearEstablished = localize.getText("notValidField", {
            fieldName: localize.getText("yearEstablished"),
          });
        }
        if (!values.businessLogo) {
          errors.businessLogo = localize.getText(
            "generalRequiredFieldValidation",
            { fieldName: localize.getText("businessLogo") }
          );
        }
        if (!values.businessImage) {
          errors.businessImage = localize.getText(
            "generalRequiredFieldValidation",
            { fieldName: localize.getText("businessImage") }
          );
        }

        return errors;
      }}
      onSubmit={handleSubmitForm}
    >
      {({ handleSubmit }) => (
        <Row>
          <Col md={8}>
            <Card>
              <Card.Body>
                <TitlePage text="Informasi Umum" />
                <form className="py-4 d-flex flex-column gap-3">
                  <Field name="businessName">
                    {({ input, meta }) => (
                      <InputField
                        label={localize.getText("businessName")}
                        placeholder={localize.getText("placeholderField", {
                          fieldName: localize.getText("businessName"),
                        })}
                        error={meta.touched && meta.error}
                        onChange={(value) =>
                          handleChangeField("businessName", value)
                        }
                        onBlur={input.onBlur}
                        value={input.value}
                      />
                    )}
                  </Field>
                  <Field name="businessType">
                    {({ input, meta }) => (
                      <AsyncSelect
                        label={localize.getText("businessType")}
                        placeholder={localize.getText("placeholderField", {
                          fieldName: localize.getText("businessType"),
                        })}
                        error={meta.touched && meta.error}
                        onChange={(value) =>
                          handleChangeField("businessType", value)
                        }
                        onBlur={input.onBlur}
                        value={input.value}
                        options={dataBusiness}
                        isLoading={false}
                      />
                    )}
                  </Field>
                  <Field name="businessService">
                    {({ input, meta }) => (
                      <AsyncSelect
                        label={localize.getText("businessService")}
                        placeholder={localize.getText("placeholderField", {
                          fieldName: localize.getText("businessService"),
                        })}
                        error={meta.touched && meta.error}
                        onChange={(value) =>
                          handleChangeField("businessService", value)
                        }
                        onBlur={input.onBlur}
                        value={input.value}
                        options={dataServices}
                        isMulti
                        isLoading={false}
                      />
                    )}
                  </Field>
                  <Field name="businessAddress">
                    {({ input, meta }) => (
                      <InputField
                        label={localize.getText("businessAddress")}
                        placeholder={localize.getText("placeholderField", {
                          fieldName: localize.getText("businessAddress"),
                        })}
                        type="textarea"
                        error={meta.touched && meta.error}
                        onChange={(value) =>
                          handleChangeField("businessAddress", value)
                        }
                        onBlur={input.onBlur}
                        value={input.value}
                      />
                    )}
                  </Field>
                  <Field name="about">
                    {({ input, meta }) => (
                      <InputField
                        label={localize.getText("aboutClinic")}
                        placeholder={localize.getText("placeholderField", {
                          fieldName: localize.getText("aboutClinic"),
                        })}
                        type="textarea"
                        error={meta.touched && meta.error}
                        onChange={(value) => handleChangeField("about", value)}
                        onBlur={input.onBlur}
                        value={input.value}
                      />
                    )}
                  </Field>
                  <Field name="yearEstablished">
                    {({ input, meta }) => (
                      <InputField
                        label={localize.getText("yearEstablished")}
                        placeholder={localize.getText("placeholderField", {
                          fieldName: localize.getText("yearEstablished"),
                        })}
                        error={meta.touched && meta.error}
                        onChange={(value) =>
                          handleChangeField("yearEstablished", value)
                        }
                        onBlur={input.onBlur}
                        value={input.value}
                        type="number"
                      />
                    )}
                  </Field>
                  <Field name="phoneNumber">
                    {({ input, meta }) => (
                      <InputField
                        label={localize.getText("phoneNumber")}
                        placeholder={localize.getText("placeholderField", {
                          fieldName: localize.getText("phoneNumber"),
                        })}
                        error={meta.touched && meta.error}
                        onChange={(value) =>
                          handleChangeField("phoneNumber", value)
                        }
                        onBlur={input.onBlur}
                        value={input.value}
                        type="number"
                      />
                    )}
                  </Field>
                  <Field name="primaryColor">
                    {({ input }) => (
                      <PrimaryColorField
                        label="Warna Primary"
                        value={input.value}
                        onChange={(value) =>
                          handleChangeField("primaryColor", value)
                        }
                      />
                    )}
                  </Field>
                  <Field name="published">
                    {({ input }) => (
                      <CheckboxField
                        id="published-bussines"
                        label="Aktifkan fitur reservasi online"
                        value={input.value}
                        onChange={(value) =>
                          handleChangeField("published", value)
                        }
                      />
                    )}
                  </Field>
                  <Field name="successReservationUrl">
                    {({ input, meta }) => (
                      <InputField
                        label={localize.getText("customUrlSuccessReservation")}
                        placeholder={
                          "https://sistemklinik.com/reservasi-berhasil"
                        }
                        error={meta.touched && meta.error}
                        onChange={(value) =>
                          handleChangeField("successReservationUrl", value)
                        }
                        onBlur={input.onBlur}
                        value={input.value}
                      />
                    )}
                  </Field>
                  <Field name="gtmId">
                    {({ input, meta }) => (
                      <InputField
                        label={localize.getText("gtmId")}
                        placeholder={localize.getText("placeholderGtmField")}
                        error={meta.touched && meta.error}
                        onChange={(value) => handleChangeField("gtmId", value)}
                        onBlur={input.onBlur}
                        value={input.value}
                      />
                    )}
                  </Field>
                  <Button
                    label={localize.getText("save")}
                    className="mt-3"
                    onClick={handleSubmit}
                    loading={isLoadingSubmit || isLoadingSubmitBusinessProfile}
                  />
                </form>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <Card.Body className="d-flex flex-column gap-4">
                <TitlePage text="Media Klinik" />
                <div className="d-flex justify-content-center flex-column gap-5 align-items-center">
                  <Field name="businessLogo">
                    {({ input, meta }) => (
                      <UploadImageAvatar
                        label="Logo Klinik"
                        defaultImages="https://dummyimage.com/400x400/F6F6F6/000"
                        id="logo-clinic"
                        value={input.value}
                        error={meta.touched && meta.error}
                        onChange={(value) =>
                          handleChangeField("businessLogo", value)
                        }
                      />
                    )}
                  </Field>
                  <Field name="businessImage">
                    {({ input, meta }) => (
                      <UploadImageAvatar
                        label="Banner Klinik"
                        defaultImages="https://dummyimage.com/600x400/F6F6F6/000"
                        style={{ width: "100%" }}
                        id="banner-clinic"
                        value={input.value}
                        error={meta.touched && meta.error}
                        onChange={(value) =>
                          handleChangeField("businessImage", value)
                        }
                      />
                    )}
                  </Field>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Form>
  );
}
